import { REFERRAL, CONTACTS, APPOINTMENT_STATUS, LEAD_ACCEPTANCE, APPOINTMENTS, FILTER_FIELD } from '_constants';
import _ from 'lodash';
import { batch } from 'react-redux';
import { fetchMemberContacts, fetchAssignedContacts, fetchContact, fetchContactProperties, fetchReferralsCountForAgent } from '../services/referrals.services';
import { isResponseSuccess } from 'helpers';
import { fetchContactOpportunities, fetchOpportunityDocuments, fetchSystemTypes, saveOpportunityNote, fetchVettingQuestions, fetchOpportunityByContactId } from '../services/opportunities.services';
import { fetchMemberAppointments } from '../services/appointments.services';
import { getDocumentMappingsForContact } from '../services';
import { redirectToRoute } from './common.actions';
import { prioritizeOpportunities, prioritizeAppointments, toggleReadLoader } from 'actions';

import moment from 'moment';

const fetch = () => async (dispatch, getState) => {
  const memberKey = _.get(getState(), 'userprofile.memberKey', '');
  const response = await dispatch(fetchMemberContacts(memberKey, 'Notes,Groups,Addresses,EmailAddresses,PhoneNumbers,Assignment,LeadSources,Opportunities', null, null, null, 'firstName'));
  if (isResponseSuccess(response)) {
    dispatch({ type: REFERRAL.ACTION.UPDATE_REFERRALS, value: response.data });
  }

  return response;
};

const fetchAssignedContactsForAgents = (page = null, pageSize = null, searchValue = null, filterType) => async (dispatch, getState) => {
  const memberKey = _.get(getState(), 'userprofile.memberKey', '');
  const sortValue = page === 0 ? 'timestampmodified desc' : 'firstname asc';
  let start = null; let end = null;
  if (filterType && !_.isEqual(filterType, FILTER_FIELD.ACTIVE_REFERRAL)) {
    start = moment().subtract(1, 'year').startOf('day').format('MM/DD/YYYY');
    end = moment().endOf('day').format('MM/DD/YYYY');
  }
  const response = await dispatch(fetchAssignedContacts(memberKey, 'Notes,Groups,Addresses,EmailAddresses,PhoneNumbers,Assignment,LeadSources,Opportunities', searchValue, filterType, sortValue, page, pageSize, start, end));
  if (isResponseSuccess(response)) {
    dispatch({ type: REFERRAL.ACTION.UPDATE_ALL_REFERRALS, value: response.data });
  }

  return response.data;
};

const search = (value) => {
  return { type: REFERRAL.ACTION.SEARCH, value };
};

const getReferralByAppointment = (referrals, appointment) => {
  if (!appointment || !referrals || !referrals.allReferrals || !appointment.contactIDs || appointment.contactIDs.length === 0 || !appointment.contactIDs[0]) {
    return null;
  }
  var referralId = appointment.contactIDs[0].id;
  var filteredReferrals = referrals.allReferrals.filter(r => r.contactKey === referralId);
  if (filteredReferrals.length === 0) { return null; }
  return filteredReferrals[0];
};

const getVettingQuestionsInfo = (sortedOpps, dispatch) => {
  const promises = (sortedOpps || []).map(async (val) => {
    const vattingQuestions = await dispatch(fetchVettingQuestions(val.opportunityKey));
    return {
      ...val,
      vattingQuestions: vattingQuestions.data
    };
  });
  return Promise.all(promises);
};

const initReferral = (contactKey, memberKey, appointmentId) => async dispatch => {
  const contactResponse = dispatch(fetchContact(contactKey, REFERRAL.CONTACT_EMBED));
  const opportunityResponse = dispatch(fetchContactOpportunities(contactKey, REFERRAL.OPPORTUNITIES_EMBED));
  const startDate = moment().subtract(APPOINTMENTS.MONTHS_LIMIT, 'months').startOf('month').format('MM/DD/YYYY');
  const endDate = moment().add(APPOINTMENTS.MONTHS_LIMIT, 'months').endOf('month').format('MM/DD/YYYY');
  const appointmentResponse = dispatch(fetchMemberAppointments(memberKey, startDate, endDate));
  const docsResponse = dispatch(fetchOpportunityDocuments(contactKey));
  const propsResponse = dispatch(fetchContactProperties(contactKey));
  const buySystemTypesResponse = dispatch(fetchSystemTypes('OPPORTUNITY_STATUS'));
  const sellSystemTypesResponse = dispatch(fetchSystemTypes('OPPORTUNITY_STATUS_SELLER'));
  const contactOppFlyerResponse = dispatch(getDocumentMappingsForContact(contactKey));
  if ((contactResponse === undefined) || (opportunityResponse === undefined)) { return false; }
  const resArr = await Promise.all([
    contactResponse,
    opportunityResponse,
    appointmentResponse,
    docsResponse.catch(error => { console.error(error); return { data: [], status: 200, statusText: 'OK' }; }),
    propsResponse,
    buySystemTypesResponse,
    sellSystemTypesResponse,
    contactOppFlyerResponse
  ]);

  const data = [];

  for (const index in resArr) {
    const res = resArr[index];
    if (!isResponseSuccess(res)) {
      dispatch(toggleReadLoader(false));
      return Promise.reject(resArr);
    }
    data.push(res.data);
  }

  const memberAppointments = data[2];

  let sortedAppts = _.isArray(memberAppointments) && prioritizeAppointments(memberAppointments.filter(appt => contactKey.toLowerCase() === _.get(appt, 'contactIDs[0].id', '').toLowerCase()));
  const nonTentativeAppts = (memberAppointments || []).filter(appt => appt.eventStatus.id !== APPOINTMENT_STATUS.TENTATIVE.id);
  const nonTentativeOpportunityKeys = new Set(_.flattenDeep(nonTentativeAppts.map(appt => appt.opportunityKeys)) || []);
  let sortedOpps = (prioritizeOpportunities(data[1]) || []);
  const nonTentativeOpps = sortedOpps.filter(opp => nonTentativeOpportunityKeys.has(opp.opportunityKey));
  const isFirstApptTentative = (sortedAppts || []).length > 0 ? _.get(sortedAppts[0], 'eventStatus.id', -1) === APPOINTMENT_STATUS.TENTATIVE.id : false;
  sortedAppts = isFirstApptTentative || !appointmentId ? sortedAppts : (sortedAppts || []).filter(appt => appt.id.toString() === appointmentId.toString());
  const activeAppointment = (sortedAppts || []).length > 0 ? sortedAppts[0] : null;
  sortedOpps = isFirstApptTentative ? sortedOpps : nonTentativeOpps;
  const appointmentOpps = (sortedOpps || []).filter(opp => activeAppointment && activeAppointment.opportunityKeys.indexOf(opp.opportunityKey) !== -1);
  const activeOpportunity = appointmentOpps.length > 0 ? appointmentOpps[0] : ((sortedOpps || []).length > 0 ? sortedOpps[0] : null);

  // TODO: This below line will be removed, once the backend team concat vetting question info into opportuniy response
  const sortedOpportunites = await getVettingQuestionsInfo(sortedOpps, dispatch);
  dispatch({
    type: REFERRAL.ACTION.INITIALIZE_REFERRAL,
    activeReferral: data[0],
    opportunities: sortedOpportunites,
    appointments: sortedAppts,
    activeOpportunity: activeOpportunity,
    activeAppointment: activeAppointment,
    documents: (data[3] || []),
    properties: (data[4] || []),
    buyOppStatusList: (data[5] || []).map(st => st.name),
    sellOppStatusList: (data[6] || []).map(st => st.name),
    additionalDocuments: (data[7] || []).sort(ad => _.get(ad, 'IsRead', null) === false)
  });

  dispatch(toggleReadLoader(false));
  return resArr;
};

const setActiveReferral = (referral) => (dispatch, getState) => {
  const memberAppointments = getState().appointments.allAppointments;
  const opportunities = referral.opportunities;
  const appointmentId = null;
  let sortedAppts = _.isArray(memberAppointments) && prioritizeAppointments(memberAppointments.filter(appt => referral.contactKey.toLowerCase() === _.get(appt, 'contactIDs[0].id', '').toLowerCase()));
  const nonTentativeAppts = (memberAppointments || []).filter(appt => appt.eventStatus.id !== APPOINTMENT_STATUS.TENTATIVE.id);
  const nonTentativeOpportunityKeys = new Set(_.flattenDeep(nonTentativeAppts.map(appt => appt.opportunityKeys)) || []);
  let sortedOpps = (prioritizeOpportunities(opportunities) || []);
  const nonTentativeOpps = sortedOpps.filter(opp => nonTentativeOpportunityKeys.has(opp.opportunityKey));
  const activeAppointment = (sortedAppts || []).length > 0 ? sortedAppts[0] : null;
  const isFirstApptTentative = (sortedAppts || []).length > 0 ? _.get(sortedAppts[0], 'eventStatus.id', -1) === APPOINTMENT_STATUS.TENTATIVE.id : false;
  sortedAppts = isFirstApptTentative || !appointmentId ? sortedAppts : (sortedAppts || []).filter(appt => appt.id.toString() === appointmentId.toString());
  sortedOpps = isFirstApptTentative ? sortedOpps : nonTentativeOpps;
  const appointmentOpps = (sortedOpps || []).filter(opp => activeAppointment && activeAppointment.opportunityKeys.indexOf(opp.opportunityKey) !== -1);
  const activeOpportunity = appointmentOpps.length > 0 ? appointmentOpps[0] : ((sortedOpps || []).length > 0 ? sortedOpps[0] : null);

  if (_.get(activeAppointment, 'eventStatus.id', false) === APPOINTMENT_STATUS.TENTATIVE.id) {
    batch(() => {
      dispatch({ type: LEAD_ACCEPTANCE.ACTION.SET_LEAD_ACCEPTANCE,
        contact: referral,
        activeAppointment: activeAppointment,
        activeOpportunity: activeOpportunity
      });
      dispatch(redirectToRoute(`/leadacceptance/${activeOpportunity.contactKey}/opportunity/${activeOpportunity.opportunityKey}/appointment/${activeAppointment.id}/`));
    });
  } else {
    batch(() => {
      dispatch(redirectToRoute(`/referraldetails/${activeOpportunity.contactKey}/`));
      dispatch({
        type: REFERRAL.ACTION.SET_ACTIVE_REFERRAL,
        payload: referral,
        opportunities: sortedOpps,
        activeAppointment: activeAppointment,
        activeOpportunity: activeOpportunity
      });
    });
  }
};

const setActiveOpportunity = (opportunity) => dispatch => {
  dispatch({
    type: REFERRAL.ACTION.SET_ACTIVE_OPPORTUNITY,
    payload: opportunity
  });
};

const updateReferralDetailCard = (currCard) => dispatch => {
  dispatch({
    type: REFERRAL.ACTION.UPDATE_REFERRAL_DETAIL_CARD,
    card: currCard
  });
};

const resetReferralDetail = () => dispatch => {
  dispatch({
    type: REFERRAL.ACTION.RESET_REFERRAL_DETAIL
  });
};

const addContactNote = (data) => async (dispatch) => {
  dispatch({ type: REFERRAL.ACTION.START_UPDATING_OPPORTUNITY });
  try {
    const response = await dispatch(saveOpportunityNote(data));
    if (isResponseSuccess(response)) {
      dispatch({ type: REFERRAL.ACTION.UPDATING_OPPORTUNITY, payload: response.data });
    }
    return response;
  } catch (err) {
    console.error(err);
    return Promise.reject(err);
  } finally {
    dispatch({ type: REFERRAL.ACTION.END_UPDATING_OPPORTUNITY });
  }
};

const changeReferralFilter = (filterKey) => dispatch => {
  dispatch({
    type: REFERRAL.ACTION.GET_FILTERED_REFFERAL,
    payload: filterKey
  });
};

const fetchContactData = (contactId) => async (dispatch) => {
  dispatch({ type: REFERRAL.ACTION.START_FETCH_CONTACT_DATA });
  try {
    const response = await dispatch(fetchContact(contactId, 'Addresses'));
    if (isResponseSuccess(response)) {
      dispatch({ type: REFERRAL.ACTION.FETCH_CONTACT_INFO, value: response.data });
    }
    return response;
  } catch (err) {
    console.error(err);
    return Promise.reject(err);
  } finally {
    dispatch({ type: REFERRAL.ACTION.END_FETCH_CONTACT_DATA });
  }
};

const fetchOpportunity = (contactId, opportunityId) => async (dispatch) => {
  try {
    dispatch({ type: REFERRAL.ACTION.START_OPPORTUNITY_FETCH });
    const contact = await dispatch(fetchContact(contactId, null));
    const response = await dispatch(fetchOpportunityByContactId(contactId, opportunityId));
    const buySystemTypesResponse = await dispatch(fetchSystemTypes('OPPORTUNITY_STATUS'));
    const sellSystemTypesResponse = await dispatch(fetchSystemTypes('OPPORTUNITY_STATUS_SELLER'));
    if ((contact === undefined) || (response === undefined) || (buySystemTypesResponse === undefined) || (sellSystemTypesResponse === undefined)) { return false; }
    dispatch({ type: CONTACTS.UPDATING_CONTACTS, payload: contact.data });
    dispatch({ type: REFERRAL.ACTION.SET_ACTIVE_OPPORTUNITY,
      payload: response.data,
      buyOppStatusList: (buySystemTypesResponse.data || []).map(status => status.name),
      sellOppStatusList: (sellSystemTypesResponse.data || []).map(status => status.name)
    });

    if (!isResponseSuccess(response)) {
      dispatch({ type: REFERRAL.OPPORTUNITY_FETCH_ERROR, value: true });
    }
    return response;
  } catch (err) {
    console.error(err);
    return Promise.reject(err);
  } finally {
    dispatch({ type: REFERRAL.ACTION.END_OPPORTUNITY_FETCH });
  }
};

const fetchReferralsCount = (filterType, searchValue = null) => async (dispatch, getState) => {
  try {
    const memberKey = _.get(getState(), 'userprofile.memberKey', '');
    let start = null; let end = null;
    if (filterType && !_.isEqual(filterType, FILTER_FIELD.ACTIVE_REFERRAL)) {
      start = moment().subtract(1, 'year').startOf('day').format('MM/DD/YYYY');
      end = moment().endOf('day').format('MM/DD/YYYY');
    }
    const response = await dispatch(fetchReferralsCountForAgent(memberKey, filterType, searchValue, start, end));
    return response.data;
  } catch (err) {
    console.error(err);
    return Promise.reject(err);
  }
};

export const referralActions = {
  fetch,
  search,
  getReferralByAppointment,
  initReferral,
  updateReferralDetailCard,
  resetReferralDetail,
  addContactNote,
  changeReferralFilter,
  fetchContactData,
  fetchOpportunity,
  setActiveReferral,
  setActiveOpportunity,
  fetchReferralsCount,
  fetchAssignedContactsForAgents
};
